import { useState, useEffect, useRef } from "react";
import { useFeedback } from "../components/ActionFeedback";
import API from "../api/axios";
import Lottie from "lottie-react";
import noDataAnimation from "../layout/no_data.json";

export default function Settings() {
  const { showToast, showModal, closeModal } = useFeedback();
  const [activeTab, setActiveTab] = useState("media");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formFields, setFormFields] = useState([
    { id: 1, name: "Full Name", required: true, fixed: true },
    { id: 2, name: "Email Address", required: true, fixed: true },
    { id: 3, name: "Phone Number", required: true, fixed: false },
    { id: 4, name: "Company", required: false, fixed: false },
    { id: 5, name: "Job Title", required: false, fixed: false },
    { id: 6, name: "Dietary Requirements", required: false, fixed: false }
  ]);

  // Settings state - mirrors the backend model
  const [settings, setSettings] = useState({
    portalName: "",
    primaryColor: "",
    globalCapacityCap: 1000,
    waitlistBuffer: 10,
    maintenanceMode: false,
    autoConfirm: true,
    emailTriggers: {
      registrationConfirmation: true,
      capacityAlert: true,
      reminderEmail: false,
    },
    supportEmail: "info@bilimbe.in",
  });

  const tabs = [
    { id: "general", name: "General Config" },
    // { id: "registration", name: "Registration Rules" },
    // { id: "form", name: "Form Fields" },
    // { id: "notifications", name: "Notifications" },
    { id: "media", name: "Mobile Media" },
  ];

  const [videos, setVideos] = useState([]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newVideo, setNewVideo] = useState({ title: "", artist: "", videoUrl: "" });
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [selectedThumbFile, setSelectedThumbFile] = useState(null);

  const carouselRef = useRef(null);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const amount = 300;
      carouselRef.current.scrollBy({ left: direction === 'right' ? amount : -amount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await API.get("/settings");
      setSettings(prev => ({ ...prev, ...res.data }));
      fetchVideos();
    } catch (err) {
      showToast("Failed to load settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    setVideoLoading(true);
    try {
      const res = await API.get("/videos");
      setVideos(res.data);
    } catch (err) {
      showToast("Failed to load videos", "error");
    } finally {
      setVideoLoading(false);
    }
  };

  const uploadFileRaw = async (file) => {
    if (!file) return null;
    const res = await API.post("/videos/upload", file, {
      headers: {
        "Content-Type": file.type,
        "x-filename": file.name,
      },
    });
    return res.data.fileId;
  };

  const handleCreateVideo = async () => {
    if (!newVideo.title) {
      showToast("Title is required", "error");
      return;
    }
    if (!selectedVideoFile && !newVideo.videoUrl.trim()) {
      showToast("Provide either a Video File or a Video URL", "error");
      return;
    }

    setUploading(true);
    try {
      const videoFileId = selectedVideoFile ? await uploadFileRaw(selectedVideoFile) : null;
      const thumbnailFileId = selectedThumbFile ? await uploadFileRaw(selectedThumbFile) : null;

      await API.post("/videos", {
        ...newVideo,
        videoUrl: newVideo.videoUrl.trim() || undefined,
        videoFileId: videoFileId || undefined,
        thumbnailFileId: thumbnailFileId || undefined,
      });

      showToast("Video added successfully", "success");
      setNewVideo({ title: "", artist: "", videoUrl: "" });
      setSelectedVideoFile(null);
      setSelectedThumbFile(null);
      fetchVideos();
    } catch (err) {
      showToast("Failed to upload video", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteVideo = (vid) => {
    showModal({
      title: "Delete Video",
      subtitle: vid.title,
      content: (
        <p className="text-slate-500 text-sm font-bold">
          Are you sure you want to delete <span className="text-slate-800">"{vid.title}"</span>? This action cannot be undone.
        </p>
      ),
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          await API.delete(`/videos/${vid._id}`);
          showToast("Video deleted", "success");
          fetchVideos();
        } catch (err) {
          showToast("Failed to delete video", "error");
        }
      }
    });
  };

  const handlePreview = (vid) => {
    const src = vid.videoFileId
      ? `${API.defaults.baseURL}/videos/stream/${vid.videoFileId}`
      : vid.videoUrl;
    if (!src) {
      showToast('No video source available for preview', 'error');
      return;
    }
    showModal({
      title: vid.title,
      subtitle: vid.artist,
      content: (
        <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
          <video 
            src={src}
            controls 
            autoPlay 
            className="w-full h-full"
          />
        </div>
      ),
      // confirmText: "Close Preview",
      // onConfirm: () => {}
    });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await API.put("/settings", settings);
      showToast("Global configuration settings saved successfully.", "success");
    } catch (err) {
      showToast("Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleEmailTrigger = (key) => {
    setSettings(prev => {
      const next = {
        ...prev,
        emailTriggers: { ...prev.emailTriggers, [key]: !prev.emailTriggers[key] }
      };
      showToast(`Notification ${next.emailTriggers[key] ? 'enabled' : 'disabled'}.`, "info");
      return next;
    });
  };

  if (loading) {
    return (
      <div className="space-y-12 animate-slide-up">
        <div className="flex justify-between items-center px-2">
          <div>
            <h2 className="text-white text-3xl font-black uppercase tracking-tight">System Settings</h2>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-2 px-1">Global Registration Configuration</p>
          </div>
        </div>
        <div className="puzzle-card-light p-20 text-center text-slate-400 font-bold uppercase tracking-widest animate-pulse">
          Loading settings...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12 animate-slide-up">
      <div className="flex justify-between items-center px-2">
        <div>
          <h2 className="text-white text-3xl font-black uppercase tracking-tight">System Settings</h2>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-2 px-1">Global Registration Configuration</p>
        </div>
        <button 
          onClick={handleSaveAll}
          disabled={saving}
          className={`${saving ? 'bg-white/60 cursor-not-allowed' : 'bg-white hover:scale-105'} text-[#0a0c10] px-10 py-4 rounded-full text-xs font-black shadow-2xl active:scale-95 transition-all uppercase tracking-widest`}
        >
          {saving ? "Saving..." : "Save All Changes"}
        </button>
      </div>

      <div className="puzzle-card-light !p-0 overflow-hidden shadow-2xl border-none">
        <div className="flex flex-col md:flex-row min-h-[600px]">
          {/* Sidebar Nav */}
          <div className="w-full md:w-64 shrink-0 p-8 border-b md:border-b-0 md:border-r border-slate-100 bg-white space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full text-left px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                  activeTab === tab.id
                    ? "bg-[#0a0c10] text-white shadow-xl shadow-[#0a0c10]/20 translate-x-2"
                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="flex-1 p-8 md:p-12">
            {activeTab === "general" && (
            <div className="space-y-10 animate-fade-in">
              <div>
                <h3 className="text-slate-800 text-xl font-black uppercase tracking-tight mb-6">Portal Branding</h3>
                <div className="grid grid-cols-2 gap-8">
                  {/* <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Portal Name</label>
                    <input
                      type="text"
                      value={settings.portalName || ""}
                      onChange={(e) => updateField("portalName", e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Primary Color (Hex)</label>
                    <div className="flex gap-3 items-center">
                      <input
                        type="color"
                        value={settings.primaryColor || "#000000"}
                        onChange={(e) => updateField("primaryColor", e.target.value)}
                        className="w-12 h-12 rounded-xl border border-slate-200 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={settings.primaryColor || ""}
                        onChange={(e) => updateField("primaryColor", e.target.value)}
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200"
                      />
                    </div>
                  </div> */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Support Email (Public)</label>
                    <input
                      type="email"
                      value={settings.supportEmail || ""}
                      onChange={(e) => updateField("supportEmail", e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200"
                      placeholder="info@bilimbe.in"
                    />
                  </div>
                </div>
              </div>

              {/* <div className="pt-10 border-t border-slate-100">
                <h3 className="text-slate-800 text-xl font-black uppercase tracking-tight mb-6">Maintenance Mode</h3>
                <div className="flex items-center justify-between p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <div>
                    <p className="text-sm font-black text-slate-800 uppercase">
                      Offline for Maintenance
                      {settings.maintenanceMode && <span className="ml-3 text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full">ACTIVE</span>}
                    </p>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-tight mt-1">Disables all public registration forms</p>
                  </div>
                  <div 
                    onClick={() => {
                      const next = !settings.maintenanceMode;
                      updateField("maintenanceMode", next);
                      showToast(next ? "Maintenance mode activated." : "Portal is now live.", next ? "error" : "success");
                    }}
                    className={`w-12 h-6 ${settings.maintenanceMode ? 'bg-[#0a0c10]' : 'bg-slate-300'} rounded-full relative cursor-pointer transition-colors`}
                  >
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${settings.maintenanceMode ? 'right-1' : 'left-1'}`}></div>
                  </div>
                </div>
              </div> */}
            </div>
          )}

          {activeTab === "registration" && (
            <div className="space-y-8 animate-fade-in">
              <h3 className="text-slate-800 text-xl font-black uppercase tracking-tight mb-6">Execution Logic</h3>
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Global Capacity Cap</label>
                  <input
                    type="number"
                    value={settings.globalCapacityCap}
                    onChange={(e) => updateField("globalCapacityCap", parseInt(e.target.value))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Waitlist Buffer (%)</label>
                  <input
                    type="number"
                    value={settings.waitlistBuffer}
                    onChange={(e) => updateField("waitlistBuffer", parseInt(e.target.value))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800"
                  />
                </div>
              </div>
              
              <div 
                onClick={() => {
                  const next = !settings.autoConfirm;
                  updateField("autoConfirm", next);
                  showToast(next ? "Auto-Confirm enabled." : "Auto-Confirm disabled.", "info");
                }}
                className={`flex items-center justify-between p-6 rounded-2xl border cursor-pointer transition-colors ${settings.autoConfirm ? 'bg-green-50/50 border-green-100' : 'bg-slate-50 border-slate-100'}`}
              >
                <div>
                  <p className={`text-sm font-black uppercase ${settings.autoConfirm ? 'text-green-800' : 'text-slate-800'}`}>Enable Auto-Confirm</p>
                  <p className={`text-xs font-bold uppercase tracking-tight mt-1 ${settings.autoConfirm ? 'text-green-600/70' : 'text-slate-400'}`}>Automatically confirm attendees when capacity is available</p>
                </div>
                <div className={`w-12 h-6 rounded-full relative transition-colors ${settings.autoConfirm ? 'bg-green-500' : 'bg-slate-300'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${settings.autoConfirm ? 'right-1' : 'left-1'}`}></div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "form" && (
            <div className="space-y-6 animate-fade-in">
              <h3 className="text-slate-800 text-xl font-black uppercase tracking-tight mb-6">Active Form Fields</h3>
              {formFields.map((field) => (
                <div key={field.id} className="flex items-center justify-between p-4 border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <span className="text-slate-300">⠿</span>
                    <span className="text-xs font-black text-slate-700 uppercase">{field.name}</span>
                  </div>
                  <div className="flex gap-3">
                    <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-md ${field.fixed ? 'bg-slate-200 text-slate-600' : (field.required ? 'bg-blue-50 text-blue-500 border border-blue-100' : 'bg-slate-100 text-slate-400')}`}>
                      {field.fixed ? 'Fixed' : (field.required ? 'Required' : 'Optional')}
                    </span>
                    {!field.fixed && (
                      <span 
                        onClick={() => {
                          showModal({
                            title: "Edit Form Field",
                            subtitle: `Modifying ${field.name}`,
                            content: (
                              <form className="space-y-4" onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target);
                                setFormFields(formFields.map(f => 
                                  f.id === field.id ? { ...f, name: formData.get("name"), required: formData.get("required") === 'true' } : f
                                ));
                                showToast("Field updated.", "success");
                                closeModal();
                              }}>
                                <div className="space-y-2">
                                  <label className="text-[10px] font-black uppercase text-slate-400">Field Name</label>
                                  <input name="name" type="text" defaultValue={field.name} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800" required />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] font-black uppercase text-slate-400">Requirement</label>
                                  <select name="required" defaultValue={field.required ? 'true' : 'false'} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 outline-none">
                                    <option value="true">Required</option>
                                    <option value="false">Optional</option>
                                  </select>
                                </div>
                                <button type="submit" className="w-full bg-[#0a0c10] text-white px-8 py-4 rounded-xl text-xs font-black shadow-xl hover:bg-slate-800 mt-4 uppercase tracking-widest">
                                  Save Field
                                </button>
                              </form>
                            ),
                            confirmText: "Cancel",
                            onConfirm: () => {}
                          });
                        }}
                        className="text-[9px] font-black uppercase px-2 py-1 rounded-md bg-slate-100 text-slate-400 cursor-pointer hover:bg-slate-200"
                      >
                        Edit
                      </span>
                    )}
                  </div>
                </div>
              ))}
              <button 
                onClick={() => {
                  showModal({
                    title: "Add Custom Field",
                    subtitle: "Create a new registration form field",
                    content: (
                      <form className="space-y-4" onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.target);
                        setFormFields([
                          ...formFields,
                          { id: Date.now(), name: formData.get("name"), required: formData.get("required") === 'true', fixed: false }
                        ]);
                        showToast("Custom field added.", "success");
                        closeModal();
                      }}>
                        <div className="space-y-2">
                          <label className="text-[10px] font-black uppercase text-slate-400">Field Name</label>
                          <input name="name" type="text" placeholder="e.g. Dietary Restrictions" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800" required />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-black uppercase text-slate-400">Requirement</label>
                          <select name="required" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 outline-none">
                            <option value="false">Optional</option>
                            <option value="true">Required</option>
                          </select>
                        </div>
                        <button type="submit" className="w-full bg-[#0a0c10] text-white px-8 py-4 rounded-xl text-xs font-black shadow-xl hover:bg-slate-800 mt-4 uppercase tracking-widest">
                          Add Field
                        </button>
                      </form>
                    ),
                    confirmText: "Cancel",
                    onConfirm: () => {}
                  });
                }}
                className="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-[10px] font-black text-slate-400 uppercase tracking-widest hover:border-slate-300 hover:text-slate-500 transition-all mt-4 hover:shadow-sm bg-white"
              >
                + Add Custom Field
              </button>
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="space-y-8 animate-fade-in">
              <h3 className="text-slate-800 text-xl font-black uppercase tracking-tight mb-6">Email Triggers</h3>
              <div className="space-y-4">
                {[
                  { title: "Registration Confirmation", desc: "Sent immediately after successful sign-up", key: "registrationConfirmation" },
                  { title: "Capacity Alert", desc: "Notify admin when event reaches 90% capacity", key: "capacityAlert" },
                  { title: "Reminder Email", desc: "Sent 24 hours before event commencement", key: "reminderEmail" }
                ].map((notif) => (
                  <div 
                    key={notif.key} 
                    onClick={() => handleToggleEmailTrigger(notif.key)}
                    className="p-6 border border-slate-100 rounded-2xl flex items-center justify-between hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <div>
                      <p className="text-sm font-black text-slate-800 uppercase">{notif.title}</p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight mt-0.5">{notif.desc}</p>
                    </div>
                    <div className={`w-12 h-6 ${settings.emailTriggers[notif.key] ? 'bg-[#0a0c10]' : 'bg-slate-200'} rounded-full relative transition-colors`}>
                      <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${settings.emailTriggers[notif.key] ? 'right-1' : 'left-1'}`}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "media" && (
              <div className="space-y-10 animate-fade-in">
                <div className="flex justify-between items-center">
                  <h3 className="text-slate-800 text-xl font-black uppercase tracking-tight">Splash Screen Videos</h3>
                </div>

                {/* Upload Form */}
                <div className="p-8 bg-slate-50 rounded-3xl border border-slate-100 space-y-6">
                  <h4 className="text-xs font-black uppercase text-slate-400 tracking-widest">Upload New Video</h4>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Title</label>
                      <input
                        type="text"
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        placeholder="e.g. Sangamam 2024"
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Category</label>
                      <input
                        type="text"
                        value={newVideo.artist}
                        onChange={(e) => setNewVideo({ ...newVideo, artist: e.target.value })}
                        placeholder="e.g. Cultural Festival"
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Video URL</label>
                      <input
                        type="url"
                        value={newVideo.videoUrl}
                        onChange={(e) => setNewVideo({ ...newVideo, videoUrl: e.target.value })}
                        placeholder="https://example.com/video.mp4"
                        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800"
                      />
                      <p className="text-[10px] text-slate-400 font-bold px-1">Or upload a file below — URL takes priority if no file is selected</p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Thumbnail (Poster)</label>
                      <div className="relative group">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setSelectedThumbFile(e.target.files[0])}
                          className="hidden"
                          id="thumb-upload"
                        />
                        <label 
                          htmlFor="thumb-upload"
                          className="flex items-center justify-center w-full h-12 bg-white border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-slate-300 transition-all text-xs font-bold text-slate-400 group-hover:text-slate-600"
                        >
                          {selectedThumbFile ? selectedThumbFile.name : "Select Image File"}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Video File <span className="normal-case font-medium">(optional if URL is set)</span></label>
                    <div className="relative group">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => setSelectedVideoFile(e.target.files[0])}
                        className="hidden"
                        id="video-upload"
                      />
                      <label 
                        htmlFor="video-upload"
                        className="flex items-center justify-center w-full h-12 bg-white border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-slate-300 transition-all text-xs font-bold text-slate-400 group-hover:text-slate-600"
                      >
                        {selectedVideoFile ? selectedVideoFile.name : "Select Video File"}
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={handleCreateVideo}
                    disabled={uploading}
                    className="w-full py-4 bg-[#0a0c10] text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? "Uploading to GridFS..." : "Add to Splash Screen"}
                  </button>
                </div>

                {/* Video List */}
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest px-1">Active Splash Media</h4>
                  {videoLoading ? (
                    <div className="text-center py-12 text-slate-400 animate-pulse font-bold uppercase text-xs">Fetching media...</div>
                  ) : videos.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                      <div className="w-32 h-32 mb-4 opacity-50">
                        <Lottie animationData={noDataAnimation} loop={true} />
                      </div>
                      <span className="text-slate-400 font-bold uppercase text-xs">No splash videos managed yet</span>
                    </div>
                  ) : (
                    <div className="relative group/carousel">
                      {/* Left Scroll Button */}
                      <button 
                        onClick={() => scrollCarousel('left')}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white shadow-xl shadow-black/10 border border-slate-100 flex items-center justify-center text-slate-800 opacity-0 group-hover/carousel:opacity-100 hover:scale-110 hover:bg-slate-50 transition-all disabled:opacity-0"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                      </button>

                      {/* Right Scroll Button */}
                      <button 
                        onClick={() => scrollCarousel('right')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white shadow-xl shadow-black/10 border border-slate-100 flex items-center justify-center text-slate-800 opacity-0 group-hover/carousel:opacity-100 hover:scale-110 hover:bg-slate-50 transition-all disabled:opacity-0"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                      </button>

                      <div ref={carouselRef} className="flex md:justify-center overflow-x-auto pb-8 pt-4 gap-4 px-4 snap-x hide-scrollbar -mx-4">
                        {videos.map((vid, i) => {
                          const palettes = [
                            { bg: 'bg-[#ffebd6]', circle: 'bg-[#ff9e4a]', text: 'text-[#cc7227]', title: 'text-slate-800', btnText: 'text-[#ffffff]', btnBg: 'bg-[#ff9e4a]', btnHover: 'hover:bg-[#e68a3c]' },
                            { bg: 'bg-[#d8caff]', circle: 'bg-[#4c2f82]', text: 'text-[#4c2f82]', title: 'text-slate-800', btnText: 'text-[#ffffff]', btnBg: 'bg-[#4c2f82]', btnHover: 'hover:bg-[#3a2266]' },
                            { bg: 'bg-[#ffd6e4]', circle: 'bg-[#ff6b9d]', text: 'text-[#cc4473]', title: 'text-slate-800', btnText: 'text-[#ffffff]', btnBg: 'bg-[#ff6b9d]', btnHover: 'hover:bg-[#e65a88]' },
                            { bg: 'bg-[#fff2cc]', circle: 'bg-[#ffcc00]', text: 'text-[#cca300]', title: 'text-slate-800', btnText: 'text-[#ffffff]', btnBg: 'bg-[#ffcc00]', btnHover: 'hover:bg-[#e6b800]' },
                            { bg: 'bg-[#ffe1ee]', circle: 'bg-[#ff8da1]', text: 'text-[#cc5e74]', title: 'text-slate-800', btnText: 'text-[#ffffff]', btnBg: 'bg-[#ff8da1]', btnHover: 'hover:bg-[#e6758a]' },
                          ];
                          const p = palettes[i % palettes.length];

                          return (
                            <div 
                              key={vid._id} 
                              className={`shrink-0 w-48 rounded-3xl p-5 flex flex-col items-center justify-between snap-center relative group transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-slate-200/50 ${p.bg}`}
                            >
                              {/* The Darker Circle */}
                              <div className={`absolute top-10 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full ${p.circle} transition-transform duration-500 group-hover:scale-105`}></div>
                              
                              {/* Thumbnail / "Bottle" */}
                              <div className="relative z-10 w-20 h-36 mt-3 mb-5 rounded-2xl overflow-hidden shadow-2xl shadow-black/20 group-hover:scale-110 transition-transform duration-500 bg-white/20 border-2 border-white/10">
                                {vid.thumbnailFileId ? (
                                  <img 
                                    src={`${API.defaults.baseURL}/videos/stream/${vid.thumbnailFileId}`} 
                                    className="w-full h-full object-cover" 
                                    alt="thumb" 
                                  />
                                ) : (
                                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/30 backdrop-blur-sm text-white/80">
                                    <svg className="w-8 h-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-[8px] font-black tracking-widest uppercase">No Image</span>
                                  </div>
                                )}
                              </div>

                              {/* Info */}
                              <div className="text-center z-10 mb-6 px-1 w-full">
                                <h5 className={`text-sm font-black uppercase tracking-widest ${p.title} leading-snug line-clamp-2`}>{vid.title}</h5>
                                <p className={`text-[9px] font-black uppercase tracking-widest mt-2 ${p.text} opacity-80`}>{vid.artist || "750 ML"}</p>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2 w-full z-10">
                                <button 
                                  onClick={() => handlePreview(vid)} 
                                  className={`flex-1 py-3 rounded-full text-[9px] font-black uppercase tracking-widest transition-colors shadow-lg shadow-black/10 ${p.btnBg} ${p.btnText} ${p.btnHover}`}
                                >
                                  VIEW
                                </button>
                                <button 
                                  onClick={() => handleDeleteVideo(vid)} 
                                  className="w-10 h-10 shrink-0 rounded-full bg-white/40 text-red-500 flex items-center justify-center hover:bg-white hover:shadow-lg transition-all"
                                  title="Delete Video"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
